import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as GridList } from '../../../../../assets/icons/grid-list.svg';
import { ReactComponent as SlashIcon } from '../../../../../assets/icons/slash.svg';
import { ReactComponent as StopIcon } from '../../../../../assets/icons/stop-circle.svg';
import { ReactComponent as TableList } from '../../../../../assets/icons/table-list.svg';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import { PortalType } from '../../../../../config/enum';
import { usePortal } from '../../../../../contexts/PortalContext/PortalContext';
import { CommercialSessionInterface } from '../../../../../interface/charging-session.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setSelectedDevice } from '../../../../../redux/slices/DeviceManagementSlice';
import {
  disableLocationDetailsSessionFiltersApplied as disableSessionFiltersApplied,
  fetchLocationDetailsChargingSessions as fetchAllSessions,
  fetchLocationDetailsListSessionFilters as fetchListSessionFilters,
  updateLocationDetailsSessionFilters as updateSessionFilters,
  setLocationDetailsSessionSortData as setSessionSortData,
  refreshLocationDetailsChargingSessions as refreshChargingSessions,
} from '../../../../../redux/slices/LocationDetailsChargingSessionSlice';
import { setSelectedStation } from '../../../../../redux/slices/LocationManagementSidePanelSlice';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import { setSidePanelData } from '../../../../../redux/slices/TableSidePanelSlice';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import CustomActionBar from '../../../../../shared-components/custom-action-bar/CustomActionBar';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import NetworkActiveChargingSession from '../../../../../shared-components/network-active-charging-session/NetworkActiveChargingSession';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import TabMenus from '../../../../../shared-components/tab-menus/TabMenus';
import DeviceSidePanel from '../../../../../shared-components/table-side-panel/DeviceSidePanel';
import FacilityDetailsSidePanel from '../../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../../shared-components/table-side-panel/LocationSidePanel';
import TariffSidePanel from '../../../../../shared-components/table-side-panel/TariffSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../../../shared-functions/JWTDocodeFunction';
import occpEvent from '../../../../../utils/api/ocpp-events';

/**
 * Commercial Session Table Component cloned specifically for station details view.
 * @returns {any} The rendered component.
 */
function CommercialSessionTable() {
  const { t, i18n } = useTranslation(['transaction', 'sidePanel', 'account']);
  const dispatch = useAppDispatch();
  const { portalType } = usePortal();
  const isPartnerPortal = portalType === PortalType.PARTNER;
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);
  const chargingStationIds = selectedChargingPoint?.id;

  /**
   * Format duration in seconds to h m s format
   * @param {number | undefined | null} seconds - The duration in seconds
   * @returns {string} The formatted duration string
   */
  const formatDuration = (seconds: number | undefined | null): string => {
    if (seconds === undefined || seconds === null || seconds <= 0) {
      return '-';
    }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    const result: string[] = [];
    if (h > 0) result.push(`${h}h`);
    if (m > 0) result.push(`${m}m`);
    if (s > 0 || result.length === 0) result.push(`${s}s`);

    return result.join(' ');
  };
  // @ts-ignore
  const {
    payments: sessions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
    // @ts-ignore
  } = useAppSelector((state) => state.locationDetailsChargingSession);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceDetails } = useAppSelector((state) => state.tableSidePanel);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const isStatusClicked = React.useRef(false);
  const [viewMode, setViewMode] = useState<number>(0);
  const [activeSessionIndex, setActiveSessionIndex] = useState<number>(0);
  const [cardLoading, setCardLoading] = useState<boolean>(false);

  /**
   * Handle active session card index change with 2-second loading animation
   * @param {number} newIndex - The target active session card index
   */
  const handleCardIndexChange = (newIndex: number): void => {
    if (newIndex === activeSessionIndex || cardLoading) return;
    setCardLoading(true);
    setActiveSessionIndex(newIndex);
    setTimeout(() => {
      setCardLoading(false);
    }, 2000);
  };

  useEffect(() => {
    if (activeSessionIndex >= (sessions?.length || 0)) {
      setActiveSessionIndex(0);
    }
  }, [sessions, activeSessionIndex]);

  const viewMenus = [
    {
      name: '',
      icon: <TableList />,
      activeIcon: <TableList />,
    },
    {
      name: '',
      icon: <GridList />,
      activeIcon: <GridList />,
    },
  ];

  /**
   * Fetch the session details in redux
   * @param {any} data - The data
   */
  const fetchSessions = useCallback(
    (data: any) => {
      if (getDaysBetween(data.startDate, data.endDate) > 30) {
        dispatch(
          setNotificationMessage({
            message: t('dateRangeLimitMessage'),
            status: true,
            type: 'error',
            code: 400,
          }),
        );
        return;
      }

      if (!loading) {
        const extraParameters = {
          'chargingStationIds[]': chargingStationIds,
          status: data?.status || 'inProgress',
        };
        dispatch(fetchAllSessions({ ...data, ...extraParameters }));
      }
    },
    [chargingStationIds, dispatch, loading, t],
  );

  /**
   * Function to refresh data
   */
  const handleRefresh = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...queryParameters, page, limit, ...sortValue });
  }, [filters, sortField, sortBy, page, limit, fetchSessions]);

  /**
   * Function to open device details view
   * @param {any} _ - The _
   * @param {any} newTab - The newTab
   */
  const handleOpenModelView = (_: any, newTab: boolean) => {
    if (deviceDetails) {
      dispatch(
        setSelectedDevice({
          id: deviceDetails?.id,
          name: deviceDetails?.chargingPointName,
          chargingPointSn: deviceDetails?.chargingPointSn,
          modelSeries: deviceDetails?.modelSeries,
        }),
      );

      const sourcePath =
        location.state?.source || window.location.pathname + window.location.search;
      if (newTab) {
        window.open(
          `/ev-admin/charger-management/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`,
          '_blank',
        );
      } else {
        navigate(
          `/ev-admin/charger-management/${deviceDetails.id}/device-details?searchBy=${deviceDetails.chargingPointName}`,
          { state: { source: sourcePath } },
        );
      }
    }
  };

  /**
   * Function to open station details view
   * @param {any} chargingStationDate - The chargingStationDate
   * @param {any} newTab - The newTab
   */
  const handleOpenModelStationView = (chargingStationDate: any, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id: chargingStationDate?.chargingStationId,
          name: chargingStationDate?.chargingStationName || chargingStationDate?.name,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/location-expanded/${chargingStationDate.chargingStationId}/location-dashboard?searchBy=${chargingStationDate.chargingStationName || chargingStationDate.name}&source=${location.pathname}${location.search}`;
        window.open(url, '_blank');
      } else {
        navigate(
          {
            pathname: `/ev-admin/location-management/${chargingStationDate.chargingStationId}/location-dashboard`,
            search: `?searchBy=${chargingStationDate.chargingStationName || chargingStationDate.name}`,
          },
          { state: { source: location.pathname + location.search } },
        );
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  const {
    isEditMode,
    openStatus,
    setOpenStatus,
    modalView,
    setModalView,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    onOpenLocationDetails: handleOpenModelStationView,
    onOpenDeviceDetails: handleOpenModelView,
    sessions,
  });

  /**
   * Modal box close function
   * @param {any} message - The message
   */
  const showSuccessNotification = (message: string) => {
    if (message) {
      setOpenStatus(true);
      setModalView({
        title: '',
        header: false,
        content: (
          <SuccessNotification
            title={message}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
            }}
          />
        ),
        rightCorner: '',
        notification: true,
      });
    } else {
      setOpenStatus(false);
    }
  };

  /**
   * Function to auto refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshChargingSessions({
        status: 'inProgress',
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        'chargingStationIds[]': chargingStationIds,
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    {
      label: t('OCPP Transaction Id'),
      accessorKey: 'transactionId',
    },
    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt',
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt',
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName',
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation',
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName',
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn',
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff',
    },
    {
      label: t('ocpiId'),
      accessorKey: 'ocpiId',
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh',
    },
    {
      label: t('amount'),
      accessorKey: 'totalAmount',
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds',
    },
    {
      label: t('status'),
      accessorKey: 'status',
    },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Function to export data in PDF or CSV format
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const headers = exportCurrentColumns.map((col) => col.label);

    const rows = sessions.map((row: CommercialSessionInterface) =>
      exportCurrentColumns.map((col) => {
        switch (col.accessorKey) {
          case 'transactionStartsAt':
            return row.transactionStartsAt ? ConvertUTCtoDate(row.transactionStartsAt as any) : t('na');

          case 'transactionEndsAt':
            return row.transactionEndsAt ? ConvertUTCtoDate(row.transactionEndsAt as any) : t('na');

          case 'status':
            return row.status ? t(row.status) : t('na');

          case 'durationSeconds':
            return formatDuration(row.durationSeconds) || t('na');

          case 'tariff':
            return (
              row?.tariff?.tariffId || (row?.tariff as any)?._id || (row as any)?.tariffId || t('na')
            );

          case 'facilityAccountName':
            return (row as any)?.facilityAccountName || row?.accountCustomId || t('na');

          case 'powerConsumedInKwh':
            return row?.powerConsumedInKwh ? Number(row.powerConsumedInKwh).toFixed(3) : t('na');

          case 'totalAmount':
            return row?.totalAmount ? Number(row.totalAmount).toFixed(2) : t('na');

          default: {
            const fieldValue = (row as any)[col.accessorKey];
            if (fieldValue && typeof fieldValue === 'object') {
              return fieldValue.name || fieldValue.id || fieldValue.tariffId || t('na');
            }
            return fieldValue ?? t('na');
          }
        }
      }),
    );

    if (rows.length > 0) {
      if (type === 'csv') {
        exportCSV(headers, rows, 'commercial-sessions.csv');
      } else {
        exportPDF(headers, rows, 'commercial-sessions.pdf');
      }
    }
  };

  /**
   * Function to handle stop button click
   * @param {CommercialSessionInterface | string[]} data - session data or list of ids
   */
  const handleStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter(
        (session: any) => data.includes(session.id) || data.includes(session._id),
      );
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsStopRequestSuccess')
            : t('transactionStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to handle force stop button click
   * @param {CommercialSessionInterface | string[]} data - session data or list of ids
   */
  const handleForceStop = (data: CommercialSessionInterface | string[]) => {
    let itemsToProcess: CommercialSessionInterface[] = [];

    if (Array.isArray(data)) {
      itemsToProcess = sessions.filter(
        (session: any) => data.includes(session.id) || data.includes(session._id),
      );
    } else {
      itemsToProcess = [data];
    }

    if (itemsToProcess.length === 0) return;

    const promises = itemsToProcess.map((item) => {
      const payload = {
        chargingPointSn: item.chargingPointSn,
        transactionId: item.transactionId,
        forceStop: true,
      };
      return occpEvent.remoteStopTransaction(payload);
    });

    Promise.all(promises)
      .then(() => {
        showSuccessNotification(
          itemsToProcess.length > 1
            ? t('transactionsForceStopRequestSuccess')
            : t('transactionForceStopRequestSuccess'),
        );
        handleRefresh();
        if (Array.isArray(data)) {
          setSelectedRows([]);
        }
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('errorMessage'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  const column = [
    {
      label: t('OCPP Transaction Id'),
      accessorKey: 'transactionId' as keyof CommercialSessionInterface,
      /**
       * Custom data for the transaction ID column
       * @param {CommercialSessionInterface} rowData session data
       * @returns {React.ReactElement} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
          }
        }
        
        return (
          <div className="transaction-id-col">
            <span>{rowData.transactionId || '-'}</span>
            {(rowData as any).isChargingPointCheck && (
              <span className={`status-badge ${statusClass}`}>
                <span className="status-text capitalize admin-badge-text">
                  Admin
                </span>
              </span>
            )}
          </div>
        );
      },
    },

    {
      label: t('sessionStartTime'),
      accessorKey: 'transactionStartsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging started at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionStartsAt as any)}</span>
      ),
    },
    {
      label: t('SessionEndTime'),
      accessorKey: 'transactionEndsAt' as keyof CommercialSessionInterface,
      sortAllow: true,
      /**
       * Custom data for the charging ended at column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{ConvertUTCtoDateTime(rowData?.transactionEndsAt as any)}</span>
      ),
    },
    {
      label: t('PartnerName'),
      accessorKey: 'facilityAccountName' as keyof CommercialSessionInterface,
      ...(!isPartnerPortal
        ? {
            queryParamsKey: 'accountCustomId',
            queryParamsValue: 'accountCustomId',
            sidePanelComponent: (
              <FacilityDetailsSidePanel
                actionActive
                isEditMode={isEditMode}
                onEditModeChange={handleEditModeChange}
                fetchIdFrom="queryParams"
                onSuccess={() => {
                  handleEditModeChange(false);
                  handleRefresh();
                }}
              />
            ),
          }
        : {}),
    },

    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof CommercialSessionInterface,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof CommercialSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('chargerSN'),
      accessorKey: 'chargingPointSn' as keyof CommercialSessionInterface,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
    },
    {
      label: t('tariffId'),
      accessorKey: 'tariff' as keyof CommercialSessionInterface,
      sortAllow: false,
      queryParamsKey: 'tariffId',
      sidePanelComponent: <TariffSidePanel fetchIdFrom="queryParams" isPartner={false} />,
      disableNewTab: true,
      /**
       * Custom data for the tariff ID column
       * @param {CommercialSessionInterface} rowData - Session data
       * @returns {any} Tariff ID element
       */
      customData: (rowData: CommercialSessionInterface) => {
        const tariffId = rowData?.tariff?.tariffId || (rowData?.tariff as any)?._id;

        if (!tariffId) {
          return <span>-</span>;
        }

        return (
          <span
            className="table-row-each-data hyperlink"
            role="presentation"
            onClick={(event) => {
              event.stopPropagation();
              const parameters = new URLSearchParams(searchParameters);
              const sidePanelKeys = [
                'accountCustomId',
                'deviceId',
                'connectorDeviceId',
                'stationId',
                'facilityId',
                'userCustomId',
                'userId',
                'utilityId',
                'transactionObjectId',
                'roleId',
                'sessionId',
                'cdrObjectId',
                'paymentDetailsId',
                'personalChargerId',
                'tariffId',
                'action',
              ];
              sidePanelKeys.forEach((key) => parameters.delete(key));
              parameters.set('tariffId', tariffId);
              setSearchParameters(parameters);
              dispatch(
                setSidePanelData({
                  key: 'Sessions',
                  value: rowData.id,
                }),
              );
            }}>
            {tariffId}
          </span>
        );
      },
    },
    {
      label: t('ocpiId'),
      accessorKey: 'ocpiId' as keyof CommercialSessionInterface,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumedInKwh' as keyof CommercialSessionInterface,
      /**
       * Custom data for the power consumed column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>
          {rowData.powerConsumedInKwh ? Number(rowData.powerConsumedInKwh).toFixed(3) : ''}
        </span>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof CommercialSessionInterface,
      /**
       * Custom data for the duration column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => (
        <span>{formatDuration(rowData.durationSeconds) || '-'}</span>
      ),
    },
    // {
    //   label: t('userName'),
    //   accessorKey: 'endConsumer' as keyof CommercialSessionInterface,
    //   sortAllow: false,
    // },

    {
      label: t('status'),
      accessorKey: 'status' as keyof CommercialSessionInterface,
      /**
       * Custom data for the status column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        let statusClass = '';
        if (rowData?.status) {
          const statusLower = rowData.status.toLowerCase();
          if (statusLower === 'finished') {
            statusClass = 'status-finished';
          } else if (
            statusLower === 'inprogress' ||
            statusLower === 'in progress' ||
            statusLower === 'in_progress'
          ) {
            statusClass = 'status-inProgress';
          } else if (statusLower === 'pending') {
            statusClass = 'status-pending';
          } else if (statusLower === 'terminated') {
            statusClass = 'status-terminated';
          } else if (statusLower === 'failed') {
            statusClass = 'status-failed';
          }
        }
        return (
          <span className={`status-badge ${statusClass}`}>
            <span className="status-text capitalize">
              {rowData?.status ? t(rowData.status) : t('na')}
            </span>
          </span>
        );
      },
    },
    {
      label: t('actions'),
      accessorKey: 'id' as keyof CommercialSessionInterface,
      sticky: 'right' as const,
      /**
       * Custom data for the actions column
       * @param {any} rowData session data
       * @returns {any} JSX element
       */
      customData: (rowData: CommercialSessionInterface) => {
        if (rowData?.status === 'inProgress') {
          const isAdminStart = (rowData as any).isChargingPointCheck === true;
          return (
            <div className={`stop-buttons ${isAdminStart ? 'disabled' : ''}`}>
              <span 
                role="presentation" 
                title={isAdminStart ? 'Session started by the admin, please go to the admin OCPP console to stop it.' : t('stop')} 
                className={isAdminStart ? 'admin-disabled-action' : ''}
              >
                <StopIcon onClick={isAdminStart ? undefined : () => handleStop(rowData)} />
              </span>
              <span 
                role="presentation" 
                title={isAdminStart ? 'Session started by the admin, please go to the admin OCPP console to stop it.' : t('forceStop')} 
                className={isAdminStart ? 'admin-disabled-action' : ''}
              >
                <SlashIcon onClick={isAdminStart ? undefined : () => handleForceStop(rowData)} />
              </span>
            </div>
          );
        }
        return <div>{t('na')}</div>;
      },
    },
  ];

  /**
   * Table pagination function
   * @param {any} value page and limit values
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const sortValue = sortField && sortBy ? { sortField, sortBy } : {}; // no sort if empty
    fetchSessions({ ...value, ...queryParameters, ...sortValue });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSessionSortData(sortData));
    fetchSessions({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  const isFirstMountFilters = React.useRef(true);

  useEffect(() => {
    if (isFirstMountFilters.current) {
      isFirstMountFilters.current = false;
      return;
    }
    if (isFilterUpdated || isStatusClicked.current) {
      isStatusClicked.current = false;
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchSessions({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableSessionFiltersApplied());
    }
  }, [filters]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchSessions({
      page: DefaultApiLimit.page,
      limit,
      ...queryParameters,
    });
  }, [chargingStationIds]);

  useEffect(() => {
    if (
      searchParameters.get('stationId') ||
      searchParameters.get('deviceId') ||
      searchParameters.get('facilityId') ||
      searchParameters.get('userCustomId')
    ) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      parameters.delete('facilityId');
      parameters.delete('userCustomId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (
      !searchParameters.get('accountCustomId') &&
      !searchParameters.get('stationId') &&
      !searchParameters.get('deviceId')
    ) {
      if (searchParameters.get('action')) {
        const parameters = new URLSearchParams(searchParameters);
        parameters.delete('action');
        setSearchParameters(parameters, { replace: true });
      }
    }
  }, [searchParameters]);

  return (
    <div className="payment-management-wrap" dir={i18n.dir()}>
      <ModalBox
        open={openStatus}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      {viewMode === 0 ? (
        <div className="table-content">
          <div className="live-sessions-view-toggle-header">
            <span className="live-sessions-header-title">
              {sessions.length > 0 && (
                <>
                  {' '}
                  {t('liveSessions')}
                  <span className="live-session-count">{activeSessionIndex + 1}</span>
                </>
              )}{' '}
            </span>
            <TabMenus openTab={viewMode} setOpenTab={setViewMode} menus={viewMenus} />
          </div>
          {cardLoading ? (
            <div
              className="card-switch-loader-container"
              style={{
                minHeight: '320px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--grey-bg, #f8fafc)',
                borderRadius: '8px',
                border: '1px solid var(--grey-4, #e2e8f0)',
              }}>
              <BatteryLoader />
            </div>
          ) : (
            <NetworkActiveChargingSession
              dataList={sessions?.[activeSessionIndex] || sessions?.[0]}
              onStopSuccess={handleRefresh}
            />
          )}
          {sessions && sessions.length > 0 && (
            <div className="card-pagination-footer">
              <div className="pagination-info">
                {t('showingSession')} {activeSessionIndex + 1}{' '}
                {t('of')} {sessions.length} (
                {totalResults || sessions.length}{' '}
                {t('totalActive')})
              </div>
              <div className="card-pagination-controls">
                <button
                  type="button"
                  className="pagination-arrow-btn"
                  disabled={cardLoading || (activeSessionIndex === 0 && page === 1)}
                  onClick={() => {
                    if (activeSessionIndex > 0) {
                      handleCardIndexChange(activeSessionIndex - 1);
                    } else if (page > 1) {
                      setCardLoading(true);
                      pageNateLimitChange({ page: page - 1, limit });
                      setActiveSessionIndex(0);
                      setTimeout(() => setCardLoading(false), 2000);
                    }
                  }}>
                  &lt;
                </button>
                {sessions.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`pagination-num-btn ${activeSessionIndex === index ? 'active' : ''}`}
                    disabled={cardLoading}
                    onClick={() => handleCardIndexChange(index)}>
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="pagination-arrow-btn"
                  disabled={
                    cardLoading ||
                    (activeSessionIndex === sessions.length - 1 && page === totalPages)
                  }
                  onClick={() => {
                    if (activeSessionIndex < sessions.length - 1) {
                      handleCardIndexChange(activeSessionIndex + 1);
                    } else if (page < totalPages) {
                      setCardLoading(true);
                      pageNateLimitChange({ page: page + 1, limit });
                      setActiveSessionIndex(0);
                      setTimeout(() => setCardLoading(false), 2000);
                    }
                  }}>
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="table-content">
          <CustomTable
            dataKey="CommercialSessions"
            version="v2"
            header={column}
            data={sessions}
            limit={limit}
            page={page}
            totalPages={totalPages}
            totalResults={totalResults}
            loading={loading}
            serialNo
            sortData={{ sortBy, sortField }}
            handleSortChange={handleSortFunction}
            pageLimitChange={pageNateLimitChange}
            exportDataPdfOrCSV={
              permissionCheck('cpoSessionExport') ? exportDataPdfOrCSV : undefined
            }
            sizeChangeButtonRequired
            filters={filters}
            filtersToDispatch={updateSessionFilters}
            filterListLoading={filterListLoading}
            filterListToDispatch={fetchListSessionFilters}
            rightTopContent={
              <TabMenus openTab={viewMode} setOpenTab={setViewMode} menus={viewMenus} />
            }
            hidedColumnNames={[
              'sessionAmount',
              'platformAmount',
              'networkRevenue',
              'sharableRevenue',
            ]}
            onHiddenColumnsChange={setHiddenColumns}
            requiredRefreshToggle
            refreshFunction={handleAutoRefresh}
            autoRefreshEnabled
            disableAutoRefreshToggle
            refreshRequired
            handleRefresh={handleRefresh}
            refershCurrentPages={handleRefresh}
            hoverLabel={t('refresh')}
            checkbox
            selectedRows={selectedRows}
            onSelectedRowsChange={setSelectedRows}
            selectKey="id"
            isRowSelectable={(row) => row.status === 'inProgress' && !(row as any).isChargingPointCheck}
            sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
            onSidePanelClose={handleSidePanelClose}
            created
            lastUpdated
            height={320}
          />
          <CustomActionBar
            selectedRows={selectedRows}
            onSelectedRowsChange={setSelectedRows}
            actions={[
              {
                label: t('stop'),
                buttonType: 'danger-outline',
                /**
                 * handle stop callback.
                 */
                onClick: () => handleStop(selectedRows),
              },
              {
                label: t('forceStop'),
                buttonType: 'error',
                /**
                 * handle force stop callback.
                 */
                onClick: () => handleForceStop(selectedRows),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

export default CommercialSessionTable;
